import React from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  CodeBracketIcon,
  CubeTransparentIcon,
  DocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PresentationChartBarIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

interface SkillAvatarProps {
  name: string;
  className?: string;
}

const getInitials = (name: string): string => {
  const words = name.trim().split(/[\s_-]+/).filter(Boolean);
  if (words.length > 1) {
    return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
  return Array.from(words[0] || '?').slice(0, 2).join('').toUpperCase();
};

const avatarTones = [
  'bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-200',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-200',
  'bg-amber-100 text-amber-800 dark:bg-amber-500/25 dark:text-amber-200',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/25 dark:text-rose-200',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/25 dark:text-cyan-200',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-200',
] as const;

const getStableTone = (name: string): string => {
  const hash = Array.from(name).reduce((sum, character) => sum + (character.codePointAt(0) ?? 0), 0);
  return avatarTones[hash % avatarTones.length];
};

const SkillAvatar: React.FC<SkillAvatarProps> = ({ name, className = '' }) => {
  const normalizedName = name.toLowerCase();
  let icon: React.ReactNode = null;
  let toneClass = getStableTone(normalizedName);

  if (/docx|word|document/.test(normalizedName)) {
    icon = <DocumentTextIcon className="h-4 w-4" />;
    toneClass = 'bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-200';
  } else if (/xlsx|excel|spreadsheet|csv/.test(normalizedName)) {
    icon = <TableCellsIcon className="h-4 w-4" />;
    toneClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-200';
  } else if (/pptx|powerpoint|slides|presentation/.test(normalizedName)) {
    icon = <PresentationChartBarIcon className="h-4 w-4" />;
    toneClass = 'bg-amber-100 text-amber-800 dark:bg-amber-500/25 dark:text-amber-200';
  } else if (/pdf/.test(normalizedName)) {
    icon = <DocumentIcon className="h-4 w-4" />;
    toneClass = 'bg-rose-100 text-rose-700 dark:bg-rose-500/25 dark:text-rose-200';
  } else if (/scheduled|schedule|cron|定时/.test(normalizedName)) {
    icon = <ClockIcon className="h-4 w-4" />;
    toneClass = 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/25 dark:text-cyan-200';
  } else if (/browser|web|网页/.test(normalizedName)) {
    icon = <GlobeAltIcon className="h-4 w-4" />;
    toneClass = 'bg-sky-100 text-sky-700 dark:bg-sky-500/25 dark:text-sky-200';
  } else if (/email|mail|imap|smtp|邮件/.test(normalizedName)) {
    icon = <EnvelopeIcon className="h-4 w-4" />;
    toneClass = 'bg-violet-100 text-violet-700 dark:bg-violet-500/25 dark:text-violet-200';
  } else if (/review|code|dev|代码/.test(normalizedName)) {
    icon = <CodeBracketIcon className="h-4 w-4" />;
    toneClass = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-200';
  } else if (/analysis|report|chart|分析|报表/.test(normalizedName)) {
    icon = <ChartBarIcon className="h-4 w-4" />;
    toneClass = 'bg-teal-100 text-teal-800 dark:bg-teal-500/25 dark:text-teal-200';
  } else if (/cad|3d|bambu|freecad|打印/.test(normalizedName)) {
    icon = <CubeTransparentIcon className="h-4 w-4" />;
    toneClass = 'bg-orange-100 text-orange-800 dark:bg-orange-500/25 dark:text-orange-200';
  }

  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-semibold ${toneClass} ${className}`}
      aria-hidden="true"
    >
      {icon ?? <span className="text-[11px] leading-none">{getInitials(name)}</span>}
    </div>
  );
};

export default SkillAvatar;
